import React, { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, Plus, Loader2, Send, Trash2, Pencil, ShieldAlert, History } from "lucide-react";
import { teamApi } from "../../lib/events";
import { api } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../../components/ui/dialog";
import { TeamActivityDrawer } from "./TeamActivityDrawer";
import TransferOwnershipDialog from "../../components/TransferOwnershipDialog";
import { toast } from "sonner";

const extractError = (e) => e?.response?.data?.detail || "Something went wrong.";
const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString("en-AU") : "—");

const STATUS_CLS = {
  accepted: "bg-emerald-600 text-white",
  pending: "bg-amber-500 text-white",
  expired: "bg-[var(--gt-bg-surface)] text-[var(--gt-ink-2)] border border-[var(--gt-line)]",
  revoked: "bg-[var(--gt-bg-surface)] text-[var(--gt-ink-3)] border border-[var(--gt-line)]",
};

function PermissionGroups({ meta, selected, setSelected, canGrantRestricted }) {
  const restricted = new Set(meta?.restricted || []);
  const toggle = (p) =>
    setSelected((prev) => (prev.includes(p) ? prev.filter((x) => x !== p) : [...prev, p]));
  return (
    <div className="space-y-3 max-h-64 overflow-y-auto border border-[var(--gt-line)] p-3" data-testid="permission-groups">
      {Object.entries(meta?.groups || {}).map(([group, perms]) => (
        <div key={group}>
          <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-[var(--gt-ink-3)] mb-1.5">{group}</div>
          <div className="grid grid-cols-2 gap-x-3 gap-y-1">
            {perms.map((p) => {
              const locked = restricted.has(p) && !canGrantRestricted;
              return (
                <label key={p} className={`flex items-center gap-2 text-xs ${locked ? "opacity-40" : "cursor-pointer"}`}>
                  <input
                    type="checkbox"
                    checked={selected.includes(p)}
                    disabled={locked}
                    onChange={() => toggle(p)}
                    data-testid={`perm-${p.replace(".", "-")}`}
                  />
                  <span className="font-mono">{p}</span>
                  {restricted.has(p) && <ShieldAlert size={11} className="text-[var(--gt-accent)]" />}
                </label>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}

function MemberModal({ eventId, meta, member, myEvents, canGrantRestricted, onClose }) {
  const isEdit = !!member;
  const [email, setEmail] = useState(member?.invited_email || "");
  const [name, setName] = useState(member?.invited_name || "");
  const [role, setRole] = useState(member?.role || "event_manager");
  const [perms, setPerms] = useState(member?.permissions || meta.roles.event_manager.permissions);
  const [extraEvents, setExtraEvents] = useState([]);
  const [saving, setSaving] = useState(false);

  const onRole = (r) => {
    setRole(r);
    setPerms(meta.roles[r]?.permissions || []);
  };

  const save = async () => {
    setSaving(true);
    try {
      if (isEdit) {
        await teamApi.update(eventId, member.id, { role, permissions: perms });
        toast.success("Permissions updated");
      } else {
        await teamApi.invite(eventId, {
          email, name: name || null, role, permissions: perms,
          event_ids: extraEvents.length ? extraEvents : null,
        });
        toast.success("Team member invited");
      }
      onClose(true);
    } catch (e) {
      toast.error(extractError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-xl max-h-[85vh] overflow-y-auto" data-testid="team-member-modal">
        <DialogHeader>
          <DialogTitle>{isEdit ? "Edit permissions" : "Add team member"}</DialogTitle>
          <DialogDescription>
            {isEdit ? member.invited_email : "They'll get an email invitation or instant access if they already have an account."}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 text-sm">
          {!isEdit && (
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="gt-form-label">Email address *</label>
                <input className="gt-input" type="email" value={email}
                  onChange={(e) => setEmail(e.target.value)} data-testid="team-email-input" />
              </div>
              <div>
                <label className="gt-form-label">Name (optional)</label>
                <input className="gt-input" value={name}
                  onChange={(e) => setName(e.target.value)} data-testid="team-name-input" />
              </div>
            </div>
          )}
          <div>
            <label className="gt-form-label">Role</label>
            <select className="gt-input" value={role} onChange={(e) => onRole(e.target.value)} data-testid="team-role-select">
              {Object.entries(meta.roles).map(([k, v]) => (
                <option key={k} value={k}>{v.label}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="gt-form-label">Permissions</label>
            <PermissionGroups meta={meta} selected={perms} setSelected={setPerms}
              canGrantRestricted={canGrantRestricted} />
            {!canGrantRestricted && (
              <p className="text-[10px] text-[var(--gt-ink-3)] mt-1">
                Refund processing and team management can only be granted by Admin or the event owner.
              </p>
            )}
          </div>
          {!isEdit && myEvents.length > 0 && (
            <div>
              <label className="gt-form-label">Assign to additional events (optional)</label>
              <div className="max-h-28 overflow-y-auto border border-[var(--gt-line)] p-2 space-y-1" data-testid="team-extra-events">
                <label className="flex items-center gap-2 text-xs cursor-pointer font-semibold">
                  <input type="checkbox"
                    checked={extraEvents.length === myEvents.length}
                    onChange={(e) => setExtraEvents(e.target.checked ? myEvents.map((ev) => ev.id) : [])}
                    data-testid="team-all-events-checkbox" />
                  All current events
                </label>
                {myEvents.map((ev) => (
                  <label key={ev.id} className="flex items-center gap-2 text-xs cursor-pointer">
                    <input type="checkbox" checked={extraEvents.includes(ev.id)}
                      onChange={(e) => setExtraEvents((prev) =>
                        e.target.checked ? [...prev, ev.id] : prev.filter((x) => x !== ev.id))} />
                    {ev.title}
                  </label>
                ))}
              </div>
              <p className="text-[10px] text-[var(--gt-ink-3)] mt-1">Future events are never included automatically.</p>
            </div>
          )}
          <div className="flex justify-end gap-2">
            <button onClick={() => onClose()} className="gt-btn-ghost text-sm" data-testid="team-cancel-btn">Cancel</button>
            <button onClick={save} disabled={saving || (!isEdit && !email)} className="gt-btn-primary text-sm" data-testid="team-save-btn">
              {saving ? <Loader2 size={14} className="animate-spin" /> : null}
              {isEdit ? "Save permissions" : "Send invitation"}
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function EventTeam() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [meta, setMeta] = useState(null);
  const [members, setMembers] = useState(null);
  const [event, setEvent] = useState(null);
  const [myEvents, setMyEvents] = useState([]);
  const [modal, setModal] = useState(null); // "new" | member object
  const [activityMember, setActivityMember] = useState(null);
  const [error, setError] = useState("");
  // Sprint AW UX fix — in-place TransferOwnershipDialog on Team page
  const [transferOpen, setTransferOpen] = useState(false);
  // Sprint AW — Copy invite link busy state
  const [copyingLinkFor, setCopyingLinkFor] = useState(null);

  const load = () => teamApi.list(id).then(setMembers).catch((e) => setError(extractError(e)));
  const reloadEvent = () => api.get(`/events/${id}`).then((r) => setEvent(r.data)).catch(() => {});

  // Sprint AW UX fix — copy invite link for pending members (team-admin scoped, backend-authorised)
  const copyInviteLink = async (m) => {
    setCopyingLinkFor(m.id);
    try {
      const { url } = await api.get(`/events/${id}/team/${m.id}/invite-link`).then((r) => r.data);
      await navigator.clipboard.writeText(url);
      toast.success("Invite link copied to clipboard");
    } catch (e) { toast.error(extractError(e)); }
    finally { setCopyingLinkFor(null); }
  };

  useEffect(() => {
    teamApi.meta().then(setMeta).catch(() => {});
    load();
    api.get(`/events/${id}`).then((r) => setEvent(r.data)).catch(() => {});
    api.get("/events").then((r) => {
      setMyEvents((r.data || []).filter((ev) => !ev.team_role && ev.id !== id));
    }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const canGrantRestricted = useMemo(
    () => !!user && (user.is_admin || (event && event.organiser_id === user.id)),
    [user, event]);

  const resend = async (m) => {
    try { await teamApi.resend(id, m.id); toast.success("Invitation resent"); load(); }
    catch (e) { toast.error(extractError(e)); }
  };
  const remove = async (m) => {
    if (!window.confirm(`Remove ${m.invited_email} from this event's team?`)) return;
    try { await teamApi.remove(id, m.id); toast.success("Access revoked"); load(); }
    catch (e) { toast.error(extractError(e)); }
  };

  return (
    <section className="p-6 sm:p-10" data-testid="event-team-page">
      <div className="max-w-6xl mx-auto">
        <button onClick={() => navigate(-1)} className="text-xs inline-flex items-center gap-1 text-[var(--gt-ink-2)] hover:text-[var(--gt-ink)] mb-4" data-testid="team-back-btn">
          <ArrowLeft size={13} /> Back
        </button>
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-8">
          <div>
            <div className="gt-label mb-2">Event Team</div>
            <h2 className="font-heading text-3xl font-black tracking-tighter">{event?.title || "Event team"}</h2>
            <p className="text-[var(--gt-ink-2)] text-sm mt-1">
              Give people role-based access to manage this event. Access is per-event and server-enforced.
            </p>
          </div>
          {meta && (
            <button onClick={() => setModal("new")} className="gt-btn-primary text-sm" data-testid="team-add-btn">
              <Plus size={15} strokeWidth={2.5} /> Add Team Member
            </button>
          )}
        </div>

        {error && <div className="gt-card text-sm text-[var(--gt-accent)]" data-testid="team-error">{error}</div>}
        {!error && members === null && <div className="text-sm text-[var(--gt-ink-3)]">Loading…</div>}
        {/* Sprint AW — Event Owner card at top of team page */}
        {event && (
          <div className="gt-card mb-4 border-l-4 border-emerald-600" data-testid="event-owner-card">
            <div className="flex items-start justify-between gap-4">
              <div>
                <div className="gt-label mb-1">Event Owner</div>
                <div className="font-semibold text-base" data-testid="event-owner-name">{event.organiser_name || event.organiser_email || "—"}</div>
                <div className="text-xs font-mono text-[var(--gt-ink-3)]" data-testid="event-owner-email">{event.organiser_email}</div>
                <div className="mt-1">
                  <span className="inline-block text-[9px] font-bold uppercase tracking-widest px-2 py-0.5 rounded-sm bg-emerald-600 text-white" data-testid="event-owner-badge">Owner</span>
                  <span className="ml-2 text-[10px] text-[var(--gt-ink-3)]">Primary Organiser — full event access</span>
                </div>
              </div>
              {user?.is_admin && (
                <button
                  type="button"
                  onClick={() => setTransferOpen(true)}
                  className="gt-btn-ghost text-xs inline-flex items-center gap-1"
                  data-testid="event-owner-transfer-btn"
                  title="Only Platform Admin can transfer ownership"
                >
                  Transfer Ownership →
                </button>
              )}
            </div>
          </div>
        )}
        {/* Sprint AW UX fix — in-place TransferOwnershipDialog (no redirect) */}
        {transferOpen && event && (
          <TransferOwnershipDialog
            event={event}
            onClose={() => setTransferOpen(false)}
            onDone={() => { setTransferOpen(false); reloadEvent(); load(); }}
          />
        )}
        {members && members.length === 0 && (
          <div className="gt-card text-sm text-[var(--gt-ink-2)]" data-testid="team-empty">
            No team members yet. Invite someone to help manage this event.
          </div>
        )}
        {members && members.length > 0 && (
          <div className="gt-card overflow-x-auto p-0">
            <table className="w-full text-xs" data-testid="team-table">
              <thead>
                <tr className="text-left text-[10px] uppercase tracking-widest text-[var(--gt-ink-3)] border-b-2 border-[var(--gt-ink)]">
                  <th className="py-2.5 px-3">Name</th>
                  <th className="py-2.5 px-3">Email</th>
                  <th className="py-2.5 px-3">Role</th>
                  <th className="py-2.5 px-3">Permissions</th>
                  <th className="py-2.5 px-3">Status</th>
                  <th className="py-2.5 px-3">Last Active</th>
                  <th className="py-2.5 px-3">Added By</th>
                  <th className="py-2.5 px-3">Date Added</th>
                  <th className="py-2.5 px-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {members.map((m) => (
                  <tr key={m.id} className="border-b border-[var(--gt-line)]" data-testid={`team-row-${m.id}`}>
                    <td className="py-2.5 px-3 font-semibold">{m.invited_name || "—"}</td>
                    <td className="py-2.5 px-3 font-mono">{m.invited_email}</td>
                    <td className="py-2.5 px-3">{m.role_label || m.role}</td>
                    <td className="py-2.5 px-3" title={(m.permissions || []).join(", ")}>
                      {(m.permissions || []).length} permission{(m.permissions || []).length === 1 ? "" : "s"}
                    </td>
                    <td className="py-2.5 px-3">
                      <span className={`inline-block text-[9px] font-bold uppercase tracking-widest px-2 py-0.5 rounded-sm ${STATUS_CLS[m.invitation_status] || ""}`}
                        data-testid={`team-status-${m.id}`}>
                        {m.invitation_status}
                      </span>
                    </td>
                    <td className="py-2.5 px-3">{fmtDate(m.last_active_at)}</td>
                    <td className="py-2.5 px-3">{m.invited_by_email || "—"}</td>
                    <td className="py-2.5 px-3">{fmtDate(m.created_at)}</td>
                    <td className="py-2.5 px-3">
                      <div className="flex items-center justify-end gap-2.5">
                        <button onClick={() => setActivityMember(m)} className="hover:text-[var(--gt-accent)]" title="View activity" data-testid={`team-activity-${m.id}`}>
                          <History size={13} />
                        </button>
                        {m.invitation_status !== "revoked" && (
                          <button onClick={() => setModal(m)} className="hover:text-[var(--gt-accent)]" title="Edit permissions" data-testid={`team-edit-${m.id}`}>
                            <Pencil size={13} />
                          </button>
                        )}
                        {m.invitation_status === "pending" && (
                          <button onClick={() => copyInviteLink(m)}
                                  disabled={copyingLinkFor === m.id}
                                  className="hover:text-[var(--gt-accent)]"
                                  title="Copy invite link"
                                  data-testid={`team-copy-link-${m.id}`}>
                            {copyingLinkFor === m.id ? <Loader2 size={13} className="animate-spin" /> : <span className="text-[9px] font-bold uppercase tracking-widest">Copy Link</span>}
                          </button>
                        )}
                        {m.invitation_status === "pending" && (
                          <button onClick={() => resend(m)} className="hover:text-[var(--gt-accent)]" title="Resend invitation" data-testid={`team-resend-${m.id}`}>
                            <Send size={13} />
                          </button>
                        )}
                        {m.invitation_status !== "revoked" && (
                          <button onClick={() => remove(m)} className="hover:text-[var(--gt-accent)]" title="Remove access" data-testid={`team-remove-${m.id}`}>
                            <Trash2 size={13} />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {activityMember && (
          <TeamActivityDrawer
            eventId={id}
            member={activityMember}
            onClose={() => setActivityMember(null)}
          />
        )}

        {modal && meta && (
          <MemberModal
            eventId={id}
            meta={meta}
            member={modal === "new" ? null : modal}
            myEvents={myEvents}
            canGrantRestricted={canGrantRestricted}
            onClose={(refresh) => { setModal(null); if (refresh) load(); }}
          />
        )}
      </div>
    </section>
  );
}
